import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosConfig";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Tooltip,
    Paper,
    Divider,
    Button,
    Stack
} from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

//import { handleApiError } from "../../utils/apiErrorHandler";

interface Judge {
    firstname: string;
    surname: string;
    license: string;
    type: "Elite" | "Basis";
    position: string[];
    clubName: string;
    isPreliminary: boolean;
    hasChanges: boolean;
    changeDetails?: string;
}

interface JudgesOverviewProps {
    competitionId: string;
}

const JudgesOverview: React.FC<JudgesOverviewProps> = ({ competitionId }) => {
    const [judges, setJudges] = useState<Judge[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [competitionName, setCompetitionName] = useState<string>("");

    useEffect(() => {
        const fetchJudges = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/competitions/${competitionId}/judges`);
                setJudges(response.data);
                
                // Lade auch den Wettkampfnamen für den Export
                const competitionResponse = await axios.get(`/api/competitions/${competitionId}`);
                setCompetitionName(competitionResponse.data.name);
                
                setLoading(false);
            } catch (err) {
                //handleApiError(error, 'Fehler beim Laden der Kampfrichter.');
                setError('Fehler beim Laden der Kampfrichter');
                setLoading(false);
            }
        };

        if (competitionId) {
            fetchJudges();
        }
    }, [competitionId]);

    // Gruppiere Kampfrichter nach Typ (Elite, Basis)
    const eliteJudges = judges.filter(judge => judge.type === "Elite");
    const baseJudges = judges.filter(judge => judge.type === "Basis");

    // CSV Export Funktion
    const exportToCSV = () => {
        const csvHeaders = [
            'Name',
            'Lizenz',
            'Position',
            'Verein',
            'Wettkampftyp',
            'Status',
            'Änderungen'
        ];

        const csvData = judges.map(judge => [
            `${judge.firstname} ${judge.surname}`,
            judge.license,
            judge.position.join('; '),
            judge.clubName,
            judge.type, // Elite oder Basis
            judge.isPreliminary ? 'Vorläufig' : 'Bestätigt',
            judge.hasChanges ? (judge.changeDetails || 'Änderungen vorhanden') : ''
        ]);

        const csvContent = [
            csvHeaders.join(','),
            ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `Kampfrichter_${competitionName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Print Export Funktion
    const exportToPrint = () => {
        const sortedEliteJudges = [...eliteJudges].sort((a, b) => a.clubName.localeCompare(b.clubName));
        const sortedBaseJudges = [...baseJudges].sort((a, b) => a.clubName.localeCompare(b.clubName));

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const createTableRows = (judgesList: Judge[]) => {
            return judgesList.map(judge => `
                <tr style="${judge.isPreliminary ? 'font-style: italic;' : ''}">
                    <td style="border: 1px solid #ccc; padding: 8px;">
                        ${judge.firstname} ${judge.surname}
                        ${judge.hasChanges ? ' ⚠️' : ''}
                    </td>
                    <td style="border: 1px solid #ccc; padding: 8px;">${judge.license}</td>
                    <td style="border: 1px solid #ccc; padding: 8px;">${judge.position.join(', ')}</td>
                    <td style="border: 1px solid #ccc; padding: 8px;">${judge.clubName}</td>
                    <td style="border: 1px solid #ccc; padding: 8px;">${judge.isPreliminary ? 'Vorläufig' : 'Bestätigt'}</td>
                </tr>
            `).join('');
        };

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Kampfrichter-Übersicht - ${competitionName}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    h1 { color: #333; border-bottom: 2px solid #1976d2; padding-bottom: 10px; }
                    h2 { color: #666; margin-top: 30px; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                    th { background-color: #f5f5f5; border: 1px solid #ccc; padding: 10px; text-align: left; }
                    td { border: 1px solid #ccc; padding: 8px; vertical-align: top; }
                    .summary { background-color: #f9f9f9; padding: 15px; margin: 20px 0; border-left: 4px solid #1976d2; }
                    .note { font-size: 12px; color: #666; margin-top: 20px; }
                    @media print { 
                        body { margin: 0; } 
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <h1>Kampfrichter-Übersicht</h1>
                <p><strong>Wettkampf:</strong> ${competitionName}</p>
                <p><strong>Exportiert am:</strong> ${new Date().toLocaleDateString('de-DE')}</p>
                
                <div class="summary">
                    <strong>Zusammenfassung:</strong><br>
                    Elite-Kampfrichter: ${eliteJudges.length}<br>
                    Basis-Kampfrichter: ${baseJudges.length}<br>
                    Gesamt: ${judges.length}
                </div>

                <h2>Elite-Wettkampf</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Lizenz</th>
                            <th>Position</th>
                            <th>Verein</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${createTableRows(sortedEliteJudges)}
                        ${sortedEliteJudges.length === 0 ? '<tr><td colspan="5" style="text-align: center; font-style: italic;">Keine Elite-Kampfrichter gefunden</td></tr>' : ''}
                    </tbody>
                </table>

                <h2>Basis-Wettkampf</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Lizenz</th>
                            <th>Position</th>
                            <th>Verein</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${createTableRows(sortedBaseJudges)}
                        ${sortedBaseJudges.length === 0 ? '<tr><td colspan="5" style="text-align: center; font-style: italic;">Keine Basis-Kampfrichter gefunden</td></tr>' : ''}
                    </tbody>
                </table>

                <div class="note">
                    <p><strong>Legende:</strong></p>
                    <p>• Kursiv: Vorläufige Meldungen (noch nicht abgeschickt)</p>
                    <p>• ⚠️: Änderungen vorhanden</p>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        
        // Kleiner Delay, damit der Content vollständig geladen ist
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const renderJudgesList = (judgesList: Judge[]) => {
        // Sortiere nach Verein
        const sortedJudges = [...judgesList].sort((a, b) => a.clubName.localeCompare(b.clubName));

        return (
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Lizenz</TableCell>
                            <TableCell>Position</TableCell>
                            <TableCell>Verein</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedJudges.map((judge, index) => (
                            <TableRow 
                                key={`${judge.firstname}-${judge.surname}-${judge.clubName}-${index}`}
                                sx={{
                                    fontStyle: judge.isPreliminary ? 'italic' : 'normal',
                                    '&:nth-of-type(odd)': {
                                        backgroundColor: theme => theme.palette.action.hover,
                                    }
                                }}
                            >
                                <TableCell component="th" scope="row">
                                    {judge.firstname} {judge.surname}
                                    {judge.hasChanges && (
                                        <Tooltip title={judge.changeDetails || "Änderungen vorhanden"}>
                                            <WarningIcon 
                                                color="warning" 
                                                fontSize="small" 
                                                sx={{ ml: 1, verticalAlign: 'middle' }} 
                                            />
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell>{judge.license}</TableCell>
                                <TableCell>
                                    {judge.position.map(pos => (
                                        <Chip 
                                            key={pos} 
                                            label={pos} 
                                            size="small" 
                                            sx={{ mr: 0.5, mb: 0.5 }} 
                                        />
                                    ))}
                                </TableCell>
                                <TableCell>{judge.clubName}</TableCell>
                            </TableRow>
                        ))}
                        {sortedJudges.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">Keine Kampfrichter gefunden</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    Kampfrichter-Übersicht
                </Typography>
                
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={exportToPrint}
                        size="small"
                    >
                        Drucken
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={exportToCSV}
                        size="small"
                    >
                        CSV Export
                    </Button>
                </Stack>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                    Elite-Wettkampf ({eliteJudges.length})
                </Typography>
                <Paper elevation={0} variant="outlined">
                    {renderJudgesList(eliteJudges)}
                </Paper>
            </Box>

            <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                    Basis-Wettkampf ({baseJudges.length})
                </Typography>
                <Paper elevation={0} variant="outlined">
                    {renderJudgesList(baseJudges)}
                </Paper>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Typography variant="caption">
                    Kursiv: Vorläufige Meldungen (noch nicht abgeschickt)
                </Typography>
            </Box>
        </Box>
    );
};

export default JudgesOverview;